import * as THREE from 'three';
import { BUKIT_SAWAH, ENVIRONMENTS, environmentById, type EnvironmentDef, type PropModel } from '../config/environments';
import { botThrottle } from '../sim/bot/driver';
import type { WheelPose } from '../sim/buggy';
import { createSim, disposeSim, type Sim } from '../sim/createSim';
import type { RunEvent } from '../sim/run';
import { sampleChunk } from '../sim/terrain/chunks';
import { loadGameModels, loadPropModels, loadUpgradeParts, loadVehicleModels, type GameModels, type ModelBatch, type UpgradeParts, type VehicleModels } from '../render/assets';
import { Backdrop } from '../render/backdrop';
import { BuggyView } from '../render/buggyView';
import { CameraRig } from '../render/cameraRig';
import { Landmarks } from '../render/landmarks';
import { Particles } from '../render/particles';
import { PickupsView } from '../render/pickupsView';
import { RoadsideProps } from '../render/roadsideProps';
import { Stage } from '../render/stage';
import { TerrainView } from '../render/terrainView';
import { Hud } from '../ui/hud';
import { PedalInput, isTyping } from '../ui/input';
import { Overlays } from '../ui/overlays';
import { GarageScreen } from '../ui/garage';
import { MapScreen, isUnlocked } from '../ui/map';
import { BoardScreen } from '../ui/board';
import { fetchBoard, fetchGhost, leaderboardEnabled, playerName, saveLocalName, setName, submit } from '../net/leaderboard';
import { nameProblem } from '../net/names';
import { countInputs, sanitizeGhost, type GhostRecord } from '../sim/ghostFormat';
import { deleteGhost, loadBest, loadGarage, loadGhost, loadGhostEnabled, loadProgress, loadSelectedEnv, saveBest, saveGarage, saveGhost, saveGhostEnabled, saveProgress, saveSelectedEnv } from '../ui/progress';
import { GhostReplay, recordGhost } from '../sim/ghost';
import { vehicleById } from '../config/vehicles';
import { renderGoals, todayKey } from '../ui/goals';
import { applyStatic, getLang, onLangChange, setLang, t, tId } from '../ui/i18n';
import { DAILY_BONUS, applyRun, missionMet, missionsFor, type Progress, type RunResult } from '../sim/missions';
import { buyUpgrade, buyVehicle, currentLevels, emptyGarage, garageStats, selectVehicle, type Garage, type UpgradeId, type UpgradeLevels } from '../sim/upgrades';
import type { VehicleId } from '../config/vehicles';
import { FrameDriver, interpolatedPose } from './FrameDriver';
import { Sound } from '../audio/sound';

/** Pixel-ratio cap per graphics quality. */
const MAX_DPR = { high: 2, low: 1 } as const;
const QUALITY_KEY = 'mbg-climb:quality';
const TUTORIAL_KEY = 'mbg-climb:tutorial';
const Z_AXIS = new THREE.Vector3(0, 0, 1);
const GHOST_LANE_Z = -0.75;
const SUMMARY_DELAY = 1.0; // seconds of wreck before the summary appears
const TEST_STATES = ['ready', 'active-play', 'airborne', 'low-fuel', 'paused', 'run-summary', 'garage', 'map', 'delivery', 'kopdes'] as const;

/**
 * Presentation shell. Owns the Three.js renderer, scene and camera, and draws whatever the current
 * headless simulation says. Restart swaps in a fresh Sim; everything else stays.
 */
export class Game {
  private sim: Sim;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(32, 16 / 9, 0.5, 600);
  private readonly stage: Stage;
  private terrainView: TerrainView;
  private backdrop: Backdrop;
  private buggyView: BuggyView;
  private parts: UpgradeParts | null;
  private readonly pickupsView: PickupsView;
  private landmarks: Landmarks;
  private props: RoadsideProps;
  private readonly models: GameModels;
  private readonly particles = new Particles();
  private readonly wheelWorld = new THREE.Vector3();
  private readonly contactShadow = makeContactShadow();
  private readonly cameraRig: CameraRig;
  private readonly hud = new Hud();
  private readonly overlays = new Overlays();
  private readonly garageScreen = new GarageScreen();
  private readonly mapScreen = new MapScreen();
  private readonly boardScreen = new BoardScreen();
  /** The landing screen: the run waits behind it, paused, until Play. */
  private menuVisible = true;
  /** After the name is saved: start the run (from Play) or go back to the menu (renaming). */
  private afterName: 'play' | 'menu' = 'play';
  /** The world-record ghost picked with Race #1 (replaces your own ghost in that environment). */
  private raceGhost: { record: GhostRecord; name: string } | null = null;
  /** Rank from the last leaderboard submission, shown on the summary. */
  private lastRank: { rank: number; total: number } | null = null;
  private readonly sound = new Sound();
  /** Wallet and upgrade levels (saved). Purchases apply from the next fresh run. */
  private garage: Garage;
  /** True once the garage changed the levels since the current run was created. */
  private upgradesDirty = false;
  /** Missions done and today's goal totals (saved). */
  private progress: Progress = loadProgress(todayKey());
  /** Missions already announced during the current run (paid when the run ends). */
  private readonly announced = new Set<string>();
  /** The best run's ghost, replayed in its own Sim in lockstep with the live run. */
  private ghost: GhostReplay | null = null;
  private ghostView: BuggyView | null = null;
  private readonly ghostLabel = makeGhostLabel();
  private ghostEnabled = loadGhostEnabled();
  private quality: 'high' | 'low' = initialQuality();
  /** First-run tips already shown (fuel, air, school); saved so each shows once. */
  private tipsShown = new Set<string>(readList(TUTORIAL_KEY));
  private runStartedAt = -1;
  /** Upgrade levels the current run was created with (a ghost records them). */
  private runLevels: UpgradeLevels;
  private ghostChecked = false;
  /** Bumped on every new run so a slow ghost load for an old run is dropped. */
  private ghostToken = 0;
  private readonly ghostWheel: WheelPose = { x: 0, y: 0, spin: 0 };
  private readonly input: PedalInput;
  private readonly driver: FrameDriver;
  private readonly wheelPose: WheelPose = { x: 0, y: 0, spin: 0 };
  private best: number;
  private time = 0;
  private endedAt: number | null = null;
  private hitstopRemaining = 0;
  private lastSize = '';
  private restarting: Promise<void> | null = null;
  private requestedSeed: number | null = null;

  static async create(canvas: HTMLCanvasElement): Promise<Game> {
    const garage = loadGarage();
    const env = startEnvironment();
    const stats = garageStats(garage);
    const [sim, models, vehicleModels, parts, props, backdrop, terrainView] = await Promise.all([
      createSim(env, stats),
      loadGameModels(),
      loadVehicleModels(stats.vehicle),
      loadUpgradeParts(),
      loadPropModels(env),
      Backdrop.create(env),
      TerrainView.create(env),
    ]);
    return new Game(canvas, env, sim, garage, models, vehicleModels, parts, props, backdrop, terrainView);
  }

  private constructor(
    private readonly canvas: HTMLCanvasElement,
    private env: EnvironmentDef,
    sim: Sim,
    garage: Garage,
    models: GameModels,
    vehicleModels: VehicleModels,
    parts: UpgradeParts,
    props: Partial<Record<PropModel, ModelBatch[]>>,
    backdrop: Backdrop,
    terrainView: TerrainView,
  ) {
    this.sim = sim;
    this.garage = garage;
    this.runLevels = { ...currentLevels(garage) };
    this.models = models;
    this.parts = parts;
    this.buggyView = new BuggyView(vehicleModels, sim.run.stats.vehicle, parts, currentLevels(garage));
    this.pickupsView = new PickupsView(models);
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    this.stage = new Stage(this.renderer, this.scene, env);
    this.terrainView = terrainView;
    this.backdrop = backdrop;
    this.landmarks = new Landmarks(sim.run.terrain, sim.run.spawnX, models.kmPost);
    this.props = new RoadsideProps(models, props, sim.run.terrain, env);
    this.cameraRig = new CameraRig(this.camera);
    this.scene.add(this.terrainView.group, this.backdrop.group, this.pickupsView.group, this.landmarks.group, this.props.group, this.particles.group, this.contactShadow);
    this.buggyView.addTo(this.scene);
    this.addRunUpTerrain();

    this.best = loadBest(env.id);
    this.landmarks.setBest(this.best);

    this.input = new PedalInput(this.el('#gas-pedal'), this.el('#brake-pedal'));
    this.input.onPause = () => this.togglePause();
    this.input.onRestart = () => void this.restart();
    this.overlays.onPauseRequest = () => this.togglePause(true);
    this.overlays.onResume = () => this.togglePause(false);
    this.overlays.onRestart = () => void this.restart();
    this.overlays.onGarage = () => this.openGarage();
    this.overlays.onMap = () => this.openMap();
    this.installLeaderboard();
    this.el('#map-button').addEventListener('click', () => this.openMap());
    this.mapScreen.onPick = (picked) => void this.pickEnvironment(picked);
    this.mapScreen.onClose = () => this.closeMap();
    this.hud.setEnvironmentName(env.name);
    this.sound.setAmbience(env.ambience);
    this.installSoundControls();
    this.el('#garage-button').addEventListener('click', () => this.openGarage());
    this.garageScreen.onBuy = (id) => this.buy(id);
    this.garageScreen.onVehicle = (id) => this.chooseVehicle(id);
    this.sound.setEngine(sim.run.stats.vehicle.sound);
    this.garageScreen.onClose = () => this.closeGarage();
    this.hud.setWallet(this.garage.coins);

    this.driver = new FrameDriver(
      () => this.sim.engine,
      () => this.renderer.render(this.scene, this.camera),
      (dt, alpha) => this.onFrame(dt, alpha),
      () => {
        this.sim.run.throttle = this.input.throttle;
      },
    );
    this.resize();
    this.handleEvents(this.sim.run.drainEvents());
    this.syncViews(1);
    this.installTestHooks();
    this.installMenu(); // needs the frame driver: the menu holds the run paused
    void this.prepareGhost();
  }

  start(): void {
    this.driver.start();
  }

  dispose(): void {
    this.driver.stop();
    this.input.dispose();
    disposeSim(this.sim);
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private onFrame(dt: number, alpha: number): void {
    this.time += dt;
    if (this.hitstopRemaining > 0) {
      this.hitstopRemaining -= dt;
      if (this.hitstopRemaining <= 0) this.driver.timeScale = 1;
    }
    this.resize();
    this.handleEvents(this.sim.run.drainEvents());

    if (this.sim.run.state.status === 'ended' && this.endedAt !== null && !this.overlays.summaryVisible) {
      if (this.time - this.endedAt >= SUMMARY_DELAY) this.showSummary();
    }
    this.syncViews(alpha, dt);
  }

  /** Moves every view to the current (interpolated) simulation state. */
  private syncViews(alpha: number, dt = 0): void {
    const { engine, run } = this.sim;
    interpolatedPose(engine, 'buggy', alpha, this.buggyView.chassis);
    for (let i = 0; i < 2; i++) {
      const p = run.buggy.wheelPose(i, this.wheelPose);
      this.buggyView.setWheel(i, p.x, p.y, p.spin);
    }

    const r = run.readout;
    const pos = this.buggyView.chassis.position;
    const airborne = !r.wheelGrounded[0] && !r.wheelGrounded[1];
    if (dt > 0) this.cameraRig.update(dt, pos.x, pos.y, Math.max(0, r.forwardSpeed), airborne);
    else this.cameraRig.snap(pos.x, pos.y);

    this.stage.follow(pos.x, pos.y);
    // Soft contact shadow on the track under the car; it shrinks and fades as the car leaves the ground.
    const ground = run.terrain.heightAt(pos.x);
    const lift = Math.max(0, pos.y - ground - 0.8);
    const fade = Math.max(0, 1 - lift / 4);
    this.contactShadow.position.set(pos.x, ground + 0.02, 0);
    this.contactShadow.rotation.set(-Math.PI / 2, 0, 0);
    this.contactShadow.rotateOnWorldAxis(Z_AXIS, Math.atan(run.terrain.slopeAt(pos.x)));
    this.contactShadow.scale.set((1 + lift * 0.15) * (run.stats.vehicle.view.shadowLength / 3.2), 1, 1);
    (this.contactShadow.material as THREE.MeshBasicMaterial).opacity = 0.55 * fade;
    this.backdrop.update(this.cameraRig.x, this.cameraRig.y);
    this.landmarks.update(this.cameraRig.x);
    this.props.update();
    if (dt > 0) {
      this.emitWheelDust(r, Math.max(0, r.forwardSpeed));
      this.emitExhaust();
      this.particles.update(dt);
    }
    this.pickupsView.update(run, this.cameraRig.x, this.time, dt);

    const nextFuel = run.terrain.nextFuelAfter(r.x);
    const nextFuelDistance = nextFuel && !run.isCollected(nextFuel.id) ? nextFuel.x - r.x : null;
    this.hud.update(run.state, this.best, nextFuelDistance);
    this.updateEngineSound();
    this.updateGhost(alpha);
    if (dt > 0) this.updateTips();
    if (dt > 0) this.checkMissions();
    this.publishDiagnostics();
  }

  private handleEvents(events: RunEvent[]): void {
    for (const e of events) {
      switch (e.type) {
        case 'chunkAdded':
          this.terrainView.add(e.index, e.heights);
          this.props.addChunk(e.index);
          break;
        case 'chunkRemoved':
          this.terrainView.remove(e.index);
          this.props.removeChunk(e.index);
          break;
        case 'pickup':
          if (e.kind === 'coin') {
            this.sound.coin();
            this.checkMissions();
          } else this.sound.gasCan();
          this.pickupsView.pop(e.kind, e.x, e.y);
          this.particles.burstSparks(e.x, e.y, e.kind === 'coin' ? [0xffd34d, 0xfff1a8] : [0x4cc76a, 0xd42a1e, 0xfff1a8]);
          if (e.kind === 'fuel') {
            this.hud.fuelCollected();
            this.cameraRig.addTrauma(0.12);
          }
          break;
        case 'stunt':
          this.hud.stunt(e.stunt);
          this.checkMissions();
          if (e.stunt.type === 'flip') this.sound.flip();
          else this.sound.airtime();
          this.cameraRig.addTrauma(0.15);
          break;
        case 'delivery': {
          this.hud.delivery(e.metres, e.coins);
          this.tip('school');
          this.checkMissions();
          this.sound.delivery();
          const p = this.buggyView.chassis.position;
          this.particles.burstSparks(p.x, p.y + 1, [0xffd34d, 0xd3222a, 0xfbf8f0]);
          break;
        }
        case 'landed':
          if (e.airSeconds > 0.5) this.sound.landing(e.airSeconds / 1.5);
          this.checkMissions();
          if (e.airSeconds > 0.8) this.cameraRig.addTrauma(Math.min(0.45, e.airSeconds * 0.18));
          if (e.airSeconds > 0.5) {
            const p = this.buggyView.chassis.position;
            this.particles.burstDust(p.x, this.sim.run.terrain.heightAt(p.x), 10, Math.min(1.6, 0.6 + e.airSeconds * 0.5));
          }
          break;
        case 'ended':
          this.endedAt = this.time;
          this.settleRun();
          if (e.reason === 'crash') {
            this.sound.crash();
            this.buggyView.setCrashed(true);
            const p = this.buggyView.chassis.position;
            this.particles.burstDust(p.x, this.sim.run.terrain.heightAt(p.x), 18, 1.4);
            this.cameraRig.addTrauma(0.6);
            this.hitstop(0.09);
          }
          break;
        case 'started':
          break;
      }
    }
  }

  /** Dust kicked up by the rear wheel while it drives on the ground. */
  private emitWheelDust(r: { wheelGrounded: [boolean, boolean] }, speed: number): void {
    const run = this.sim.run;
    if (run.state.status !== 'running' || !r.wheelGrounded[0] || run.throttle <= 0 || speed < 1.5) return;
    const w = run.buggy.wheelPose(0, this.wheelPose);
    this.wheelWorld.set(w.x, w.y - 0.4, 0.4);
    this.buggyView.chassis.localToWorld(this.wheelWorld);
    this.particles.wheelDust(this.wheelWorld.x, this.wheelWorld.y, speed, Math.min(1, speed / 8));
  }

  /** Tailpipe exhaust under throttle (rear bumper, low on the camera side). */
  private emitExhaust(): void {
    const run = this.sim.run;
    if (run.state.status !== 'running' || run.throttle <= 0) return;
    const tail = run.stats.vehicle.view.tailpipe;
    this.wheelWorld.set(tail.x, tail.y, tail.z);
    this.buggyView.chassis.localToWorld(this.wheelWorld);
    this.particles.exhaust(this.wheelWorld.x, this.wheelWorld.y, run.throttle);
  }

  /** Visual-only flat ground behind the start line (physics never reaches x < 0). */
  private addRunUpTerrain(): void {
    for (const index of [-2, -1]) {
      this.terrainView.add(index, sampleChunk(this.sim.run.terrain, index));
      this.props.addChunk(index);
    }
  }

  private hitstop(seconds: number): void {
    this.driver.timeScale = 0.05;
    this.hitstopRemaining = seconds;
  }

  private showSummary(): void {
    const s = this.sim.run.state;
    const newBest = s.distance > this.best;
    if (newBest) {
      this.best = s.distance;
      saveBest(this.env.id, this.best);
      this.landmarks.setBest(this.best);
    }
    renderGoals(this.el('#summary-goals'), this.env.id, this.progress);
    this.renderSummaryBoard();
    this.overlays.showSummary({
      reason: s.endReason ?? 'crash',
      distance: s.distance,
      best: this.best,
      newBest,
      coins: s.coins,
      flips: s.flips,
      wallet: this.garage.coins,
    });
  }

  /** Banks a finished run's coins into the saved wallet. */
  private runResult(): RunResult {
    const s = this.sim.run.state;
    return {
      envId: this.env.id,
      vehicle: this.garage.vehicle,
      distance: s.distance,
      coins: s.coins,
      flips: s.flips,
      deliveries: s.deliveries,
      bestAir: s.bestAir,
    };
  }

  /** Announces missions as soon as the run meets them; they are paid when the run ends. */
  private checkMissions(): void {
    if (this.sim.run.state.status !== 'running') return;
    const result = this.runResult();
    for (const m of missionsFor(this.env.id)) {
      if (this.progress.done.includes(m.id) || this.announced.has(m.id) || !missionMet(m, result)) continue;
      this.announced.add(m.id);
      this.hud.notice(t('toast.mission', { text: tId(`mission.${m.id}`, m.text), coins: m.reward }), true);
      this.sound.fanfare();
    }
  }

  /** Run ended: completes missions and daily goals, then banks the run's coins plus their rewards. */
  private settleRun(): void {
    this.saveGhostIfBest();
    const { progress, payout } = applyRun(this.progress, this.runResult(), todayKey());
    this.progress = progress;
    saveProgress(progress);
    for (const g of payout.goals) this.hud.notice(t('toast.daily', { text: tId(`daily.${g.id}`, g.text), coins: g.reward }), true);
    if (payout.bonus) this.hud.notice(t('toast.daily-all', { coins: DAILY_BONUS }), true);
    if (payout.goals.length > 0 || payout.bonus) this.sound.fanfare();
    this.bank(this.sim.run.state.coins + payout.coins);
  }

  private bank(coins: number): void {
    if (coins <= 0) return;
    this.garage = { ...this.garage, coins: this.garage.coins + coins };
    saveGarage(this.garage);
    this.hud.setWallet(this.garage.coins);
  }

  /** Opens the garage before a run or from the summary; the simulation is paused meanwhile. */
  private openGarage(): void {
    if (this.restarting || this.garageScreen.visible || this.mapScreen.visible) return;
    const status = this.sim.run.state.status;
    if (status === 'running' && !this.overlays.summaryVisible) return;
    this.driver.paused = true;
    this.garageScreen.show(this.garage);
  }

  private buy(id: UpgradeId): void {
    const next = buyUpgrade(this.garage, id);
    if (!next) return;
    this.garage = next;
    this.upgradesDirty = true;
    saveGarage(this.garage);
    this.hud.setWallet(this.garage.coins);
    this.garageScreen.render(this.garage);
    this.garageScreen.flash(id);
    this.sound.purchase();
  }

  /** Keeps the simulation paused while the main menu (or the name screen over it) is up. */
  private unpause(): void {
    this.driver.paused = this.menuVisible;
  }

  private installMenu(): void {
    this.el('#menu-play').addEventListener('click', () => this.playFromMenu());
    this.el('#menu-driver').addEventListener('click', () => this.openName('menu'));
    this.el('#menu-map').addEventListener('click', () => this.openMap());
    this.el('#menu-garage').addEventListener('click', () => this.openGarage());
    if (leaderboardEnabled) {
      this.el('#menu-board').hidden = false;
      this.el('#menu-board').addEventListener('click', () => this.openBoard());
    }
    this.el('#menu-sound').addEventListener('click', () => {
      this.el('#sound-button').click();
      this.renderMenu();
    });
    this.el('#menu-lang').addEventListener('click', () => this.el('#lang-button').click());
    this.el('#pause-menu').addEventListener('click', () => void this.showMenu());
    this.el('#summary-menu').addEventListener('click', () => void this.showMenu());
    const form = this.el<HTMLFormElement>('#name-form');
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitName();
    });
    this.el('#name-cancel').addEventListener('click', () => (this.el('#name-overlay').hidden = true));
    onLangChange(() => this.renderMenu());
    this.setMenuVisible(true);
  }

  private setMenuVisible(visible: boolean): void {
    this.menuVisible = visible;
    this.el('#menu-overlay').hidden = !visible;
    this.el('#app').classList.toggle('in-menu', visible);
    if (visible) this.renderMenu();
    this.unpause();
  }

  private renderMenu(): void {
    const name = playerName();
    this.el('#menu-driver-name').textContent = name ? t('menu.driver', { name }) : t('menu.set-name');
    this.el('#menu-env-name').textContent = this.env.name;
    const best = Math.floor(loadBest(this.env.id));
    this.el('#menu-env-best').textContent = best > 0 ? t('menu.best', { m: best }) : t('menu.no-best');
    this.el('#menu-sound').textContent = this.el('#sound-button').textContent;
    this.el('#menu-lang').textContent = t('lang.button');
  }

  /** Play: a driver name is required first; then the run waits on the start line for the gas. */
  private playFromMenu(): void {
    if (!playerName()) {
      this.openName('play');
      return;
    }
    this.setMenuVisible(false);
  }

  private openName(after: 'play' | 'menu'): void {
    this.afterName = after;
    const input = this.el<HTMLInputElement>('#name-input');
    input.value = playerName() ?? '';
    this.el('#name-error').hidden = true;
    this.el('#name-submit').textContent = t(after === 'play' ? 'name.start' : 'name.save');
    this.el('#name-overlay').hidden = false;
    input.focus();
  }

  private submitName(): void {
    const input = this.el<HTMLInputElement>('#name-input');
    const problem = nameProblem(input.value);
    const name = problem ? null : saveLocalName(input.value);
    if (!name) {
      const error = this.el('#name-error');
      error.textContent = t('board.bad-name');
      error.hidden = false;
      input.focus();
      return;
    }
    // The server learns the name now when it can (otherwise with the first leaderboard run).
    if (leaderboardEnabled) setName(name).catch(() => undefined);
    this.el('#name-overlay').hidden = true;
    this.renderMenu();
    this.renderSummaryBoard();
    if (this.afterName === 'play') this.setMenuVisible(false);
  }

  /** Back to the main menu with a fresh run waiting behind it. */
  private async showMenu(): Promise<void> {
    this.overlays.hideAll();
    this.setMenuVisible(true);
    await this.restart();
    this.renderMenu();
  }

  private installLeaderboard(): void {
    if (!leaderboardEnabled) return;
    this.el('#board-button').hidden = false;
    this.el('#summary-board').hidden = false;
    this.el('#board-button').addEventListener('click', () => this.openBoard());
    this.el('#summary-board').addEventListener('click', () => this.openBoard());
    this.boardScreen.onClose = () => {
      this.boardScreen.hide();
      if (!this.overlays.summaryVisible) this.unpause();
      this.renderSummaryBoard();
    };
    this.boardScreen.onJoin = async (name) => {
      await setName(name);
      this.boardScreen.joined();
      // Your best run so far in this environment joins the board right away.
      const best = loadGhost(this.env.id);
      if (best) {
        await submit({ envId: best.envId, distance: best.distance, coins: 0, ticks: countInputs(best.inputs), vehicle: best.vehicle, levels: best.levels, ghost: best }).catch(() => null);
      }
      this.refreshBoard();
    };
    this.boardScreen.onRace = () => {
      fetchGhost(this.env.id, 1)
        .then((res) => {
          const record = sanitizeGhost(res.ghost);
          if (!record || record.envId !== this.env.id) return;
          this.raceGhost = { record, name: res.name };
          this.boardScreen.hide();
          void this.restart();
        })
        .catch(() => this.boardScreen.offline());
    };
    onLangChange(() => this.renderSummaryBoard());
  }

  private openBoard(): void {
    if (this.restarting || this.boardScreen.visible) return;
    if (this.sim.run.state.status === 'running' && !this.overlays.summaryVisible) return;
    this.driver.paused = true;
    const hasName = playerName() !== null;
    this.boardScreen.show(this.env.id, hasName);
    if (hasName) this.refreshBoard();
  }

  private refreshBoard(): void {
    const env = this.env.id;
    fetchBoard(env)
      .then((board) => {
        if (env === this.env.id) this.boardScreen.render(board);
      })
      .catch(() => this.boardScreen.offline());
  }

  /** Sends a finished run to the leaderboard if it beats this device's best there. */
  private submitRun(record: GhostRecord, coins: number): void {
    if (!leaderboardEnabled) return;
    submit({ envId: record.envId, distance: record.distance, coins, ticks: countInputs(record.inputs), vehicle: record.vehicle, levels: record.levels, ghost: record })
      .then((res) => {
        if (!res) return;
        this.lastRank = { rank: res.rank, total: res.total };
        this.renderSummaryBoard();
      })
      .catch(() => undefined);
  }

  private renderSummaryBoard(): void {
    if (!leaderboardEnabled) return;
    const button = this.el('#summary-board');
    button.textContent = !playerName() ? t('board.join-cta') : this.lastRank ? t('board.rank', this.lastRank) : t('board');
  }

  private openMap(): void {
    if (this.restarting || this.mapScreen.visible || this.garageScreen.visible) return;
    if (this.sim.run.state.status === 'running' && !this.overlays.summaryVisible) return;
    this.driver.paused = true;
    this.mapScreen.show(this.env.id, (id) => loadBest(id));
  }

  private closeMap(): void {
    this.mapScreen.hide();
    this.unpause();
    if (this.overlays.summaryVisible) this.el('#retry-button').focus();
  }

  /** Picking the current environment just closes the map; another one is loaded and a fresh run starts there. */
  private async pickEnvironment(env: EnvironmentDef): Promise<void> {
    if (env.id === this.env.id) {
      this.mapScreen.hide();
      if (this.overlays.summaryVisible) await this.restart();
      else this.unpause();
      return;
    }
    this.mapScreen.hide();
    await this.switchEnvironment(env);
  }

  /** Swaps every environment-dependent view (terrain art, backdrop, props, lighting) and starts a fresh run. */
  private switchEnvironment(env: EnvironmentDef): Promise<void> {
    if (this.restarting) return this.restarting;
    this.driver.paused = true;
    this.restarting = (async () => {
      const stats = garageStats(this.garage);
      const [sim, vehicleModels, props, backdrop, terrainView] = await Promise.all([
        createSim(env, stats),
        loadVehicleModels(stats.vehicle),
        loadPropModels(env),
        Backdrop.create(env),
        TerrainView.create(env),
      ]);
      disposeSim(this.sim);
      this.sim = sim;
      this.runLevels = { ...currentLevels(this.garage) };
      this.rebuildVehicleView(vehicleModels);
      this.upgradesDirty = false;
      this.env = env;
      this.raceGhost = null;
      saveSelectedEnv(env.id);
      this.terrainView.dispose();
      this.backdrop.dispose();
      this.props.dispose();
      this.scene.remove(this.landmarks.group);
      this.terrainView = terrainView;
      this.backdrop = backdrop;
      this.props = new RoadsideProps(this.models, props, sim.run.terrain, env);
      this.landmarks = new Landmarks(sim.run.terrain, sim.run.spawnX, this.models.kmPost);
      this.scene.add(this.terrainView.group, this.backdrop.group, this.props.group, this.landmarks.group);
      this.stage.apply(env);
      this.best = loadBest(env.id);
      this.landmarks.setBest(this.best);
      this.hud.setEnvironmentName(env.name);
      this.sound.setAmbience(env.ambience);
      this.resetRunViews();
    })().finally(() => {
      this.restarting = null;
    });
    return this.restarting;
  }

  /** A run that went further than the stored ghost becomes the new ghost for this environment. */
  private saveGhostIfBest(): void {
    const run = this.sim.run;
    const record = recordGhost(run, this.env.id, run.stats.vehicle.id, this.runLevels);
    const stored = loadGhost(this.env.id);
    if (run.state.distance > (stored?.distance ?? 0)) saveGhost(record);
    this.submitRun(record, run.state.coins);
  }

  /** Loads this environment's ghost for the run that just started (async; dropped if a newer run began). */
  private async prepareGhost(): Promise<void> {
    const token = ++this.ghostToken;
    this.disposeGhost();
    const racing = this.raceGhost && this.raceGhost.record.envId === this.env.id ? this.raceGhost : null;
    const record = this.ghostEnabled ? (racing?.record ?? loadGhost(this.env.id)) : null;
    if (!record) return;
    const vehicle = vehicleById(record.vehicle)!;
    const env = this.env;
    const [replay, models] = await Promise.all([GhostReplay.create(record, env), loadVehicleModels(vehicle)]);
    if (token !== this.ghostToken || env !== this.env) {
      replay.dispose();
      return;
    }
    this.ghost = replay;
    this.ghostChecked = false;
    this.ghostView = new BuggyView(models, vehicle, this.parts, record.levels);
    this.ghostView.makeGhost();
    this.ghostView.addTo(this.scene);
    setGhostLabel(this.ghostLabel, racing ? t('board.racing', { name: racing.name }) : t('ghost.label', { m: Math.floor(record.distance) }));
    this.scene.add(this.ghostLabel);
  }

  private disposeGhost(): void {
    this.hud.setGhostGap(null);
    this.ghost?.dispose();
    this.ghost = null;
    this.ghostView?.dispose();
    this.ghostView = null;
    this.ghostLabel.removeFromParent();
  }

  /** Steps the ghost to the live run's tick and poses it; it waits on the start line until you set off. */
  private updateGhost(alpha: number): void {
    const ghost = this.ghost;
    const view = this.ghostView;
    if (!ghost || !view) return;
    const live = this.sim.run.state;
    if (live.status === 'running') ghost.advanceTo(live.ticks);
    const g = ghost.sim;
    interpolatedPose(g.engine, 'buggy', live.status === 'running' ? alpha : 1, view.chassis);
    // In the lane just behind the player's, so the two never overlap when driven alike.
    view.chassis.position.z = GHOST_LANE_Z;
    for (let i = 0; i < 2; i++) {
      const p = g.run.buggy.wheelPose(i, this.ghostWheel);
      view.setWheel(i, p.x, p.y, p.spin);
    }
    this.ghostLabel.position.set(view.chassis.position.x, view.chassis.position.y + 2.3, GHOST_LANE_Z);
    this.hud.setGhostGap(live.status === 'running' && !ghost.finished ? this.sim.run.readout.x - g.run.readout.x : null);
    if (ghost.finished && !this.ghostChecked) {
      this.ghostChecked = true;
      // Determinism self-check: a replay that misses its recorded distance can't be trusted.
      if (!ghost.matchesRecording()) {
        if (this.raceGhost?.record === ghost.record) this.raceGhost = null;
        else deleteGhost(this.env.id);
        this.disposeGhost();
        return;
      }
    }
    if (ghost.finished) view.setOpacity(g.run.state.status === 'ended' ? 0.5 : 1);
  }

  /** Engine pitch follows wheel speed; with the wheels in the air under throttle it revs freely. */
  private updateEngineSound(): void {
    const run = this.sim.run;
    const r = run.readout;
    const status = run.state.status;
    const airborne = !r.wheelGrounded[0] && !r.wheelGrounded[1];
    const throttle = Math.max(0, run.throttle);
    const powered = run.state.fuel > 0 && status !== 'ended';
    const speedRpm = Math.min(1, Math.abs(r.forwardSpeed) / run.stats.topSpeed);
    const rpm = !powered ? 0 : airborne && throttle > 0 ? 1 : Math.min(1, speedRpm * 0.85 + throttle * 0.2);
    this.sound.updateEngine(rpm, powered ? throttle : 0, powered && !this.driver.paused);
    this.sound.lowFuel(status === 'running' && !this.driver.paused && run.state.fuel / run.state.fuelCapacity < 0.2);
  }

  private installSoundControls(): void {
    const button = this.el('#sound-button');
    const render = () => {
      button.textContent = this.sound.muted ? t('sound.off') : t('sound.on');
      button.setAttribute('aria-pressed', String(!this.sound.muted));
    };
    const toggle = () => {
      this.sound.setMuted(!this.sound.muted);
      render();
    };
    button.addEventListener('click', toggle);
    const ghostButton = this.el('#ghost-button');
    const renderGhost = () => {
      ghostButton.textContent = this.ghostEnabled ? t('ghost.on') : t('ghost.off');
      ghostButton.setAttribute('aria-pressed', String(this.ghostEnabled));
    };
    ghostButton.addEventListener('click', () => {
      this.ghostEnabled = !this.ghostEnabled;
      saveGhostEnabled(this.ghostEnabled);
      renderGhost();
      if (!this.ghostEnabled) this.disposeGhost();
      else if (this.sim.run.state.status === 'ready') void this.prepareGhost();
    });
    renderGhost();
    // Language: English / Bahasa Indonesia. Screens that are open re-render in the new language.
    const langButton = this.el('#lang-button');
    const renderLang = () => (langButton.textContent = t('lang.button'));
    langButton.addEventListener('click', () => setLang(getLang() === 'en' ? 'id' : 'en'));
    onLangChange(() => {
      renderLang();
      render();
      renderGhost();
      renderGoals(this.el('#pause-goals'), this.env.id, this.progress);
      if (this.overlays.summaryVisible) {
        renderGoals(this.el('#summary-goals'), this.env.id, this.progress);
        this.overlays.refreshSummary();
      }
      if (this.garageScreen.visible) this.garageScreen.render(this.garage);
      if (this.ghost) setGhostLabel(this.ghostLabel, t('ghost.label', { m: Math.floor(this.ghost.record.distance) }));
    });
    // Graphics quality: High (DPR ≤ 2, sun shadows) or Low (DPR 1, no shadow map).
    const qualityButton = this.el('#quality-button');
    const renderQuality = () => (qualityButton.textContent = t(this.quality === 'high' ? 'quality.high' : 'quality.low'));
    qualityButton.addEventListener('click', () => {
      this.quality = this.quality === 'high' ? 'low' : 'high';
      writeValue(QUALITY_KEY, this.quality);
      this.applyQuality();
      renderQuality();
    });
    onLangChange(renderQuality);
    this.applyQuality();
    renderQuality();
    applyStatic();
    renderLang();
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyM' && !e.repeat && !isTyping(e)) toggle();
    });
    // UI click feedback for every button except the pedals (they have the engine).
    document.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement | null)?.closest('button');
      if (target && !target.classList.contains('pedal')) this.sound.click();
    });
    render();
  }

  private applyQuality(): void {
    this.stage.setShadows(this.quality === 'high');
    this.lastSize = ''; // force resize() to apply the new pixel-ratio cap
    this.resize();
  }

  /** First-run tips, each shown once ever: fuel a few seconds in, the first jump, the first school. */
  private tip(id: 'fuel' | 'air' | 'school'): void {
    if (this.tipsShown.has(id)) return;
    this.tipsShown.add(id);
    writeValue(TUTORIAL_KEY, JSON.stringify([...this.tipsShown]));
    this.hud.tip(t(`tip.${id}`));
  }

  private updateTips(): void {
    const run = this.sim.run;
    if (run.state.status !== 'running') return;
    if (this.runStartedAt < 0) this.runStartedAt = this.time;
    if (this.time - this.runStartedAt > 4) this.tip('fuel');
    const r = run.readout;
    if (!r.wheelGrounded[0] && !r.wheelGrounded[1] && r.y - run.terrain.heightAt(r.x) > 1.6) this.tip('air');
  }

  /** Clears per-run presentation state after a fresh Sim was swapped in. */
  private resetRunViews(): void {
    this.terrainView.clear();
    this.props.clear();
    this.addRunUpTerrain();
    this.pickupsView.clearPops();
    this.particles.clear();
    this.hud.clearToasts();
    this.buggyView.setCrashed(false);
    this.endedAt = null;
    this.lastRank = null;
    this.runStartedAt = -1;
    this.announced.clear();
    this.overlays.hideAll();
    this.garageScreen.hide();
    this.mapScreen.hide();
    this.driver.timeScale = 1;
    this.hitstopRemaining = 0;
    this.handleEvents(this.sim.run.drainEvents());
    this.syncViews(1);
    this.unpause();
    void this.prepareGhost();
  }

  /** Buys (if needed) and switches to a vehicle; the next fresh run uses it. */
  private chooseVehicle(id: VehicleId): void {
    if (id === this.garage.vehicle) return;
    const owned = this.garage.owned.includes(id);
    const next = owned ? selectVehicle(this.garage, id) : buyVehicle(this.garage, id);
    if (!next) return;
    this.garage = next;
    this.upgradesDirty = true;
    saveGarage(this.garage);
    this.hud.setWallet(this.garage.coins);
    this.garageScreen.render(this.garage);
    if (owned) this.sound.click();
    else this.sound.fanfare();
  }

  /** Replaces the vehicle view (vehicle or visible upgrades changed). */
  private rebuildVehicleView(models: VehicleModels): void {
    const vehicle = this.sim.run.stats.vehicle;
    this.buggyView.dispose();
    this.buggyView = new BuggyView(models, vehicle, this.parts, currentLevels(this.garage));
    this.buggyView.addTo(this.scene);
    this.sound.setEngine(vehicle.sound);
  }

  /** Closing before a run rebuilds it with the new upgrades; from the summary, Retry does that. */
  private closeGarage(): void {
    this.garageScreen.hide();
    if (this.overlays.summaryVisible) {
      this.unpause();
      this.el('#retry-button').focus();
    } else if (this.upgradesDirty) {
      void this.restart();
    } else {
      this.unpause();
    }
  }

  private togglePause(force?: boolean): void {
    if (this.menuVisible || this.overlays.summaryVisible || this.garageScreen.visible || this.mapScreen.visible || this.boardScreen.visible || this.restarting) return;
    const pause = force ?? !this.overlays.pauseVisible;
    if (pause) renderGoals(this.el('#pause-goals'), this.env.id, this.progress);
    this.overlays.showPause(pause);
    this.driver.paused = pause;
  }

  /** Starts a fresh run: disposes the current simulation and creates a new one from the same seed. */
  private restart(): Promise<void> {
    if (this.restarting) return this.restarting;
    this.driver.paused = true;
    this.restarting = (async () => {
      const stats = garageStats(this.garage);
      const [next, vehicleModels] = await Promise.all([createSim(this.env, stats), loadVehicleModels(stats.vehicle)]);
      disposeSim(this.sim);
      this.sim = next;
      this.runLevels = { ...currentLevels(this.garage) };
      if (this.upgradesDirty) this.rebuildVehicleView(vehicleModels);
      this.upgradesDirty = false;
      this.resetRunViews();
    })().finally(() => {
      this.restarting = null;
    });
    return this.restarting;
  }

  private resize(): void {
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR[this.quality]);
    const key = `${width}x${height}@${dpr}`;
    if (key === this.lastSize) return;
    this.lastSize = key;
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /** Simulates headlessly with the bot driver (test-state setup only). */
  private simulateBot(maxTicks: number, until?: () => boolean): void {
    const { engine, run } = this.sim;
    for (let i = 0; i < maxTicks && run.state.status !== 'ended'; i++) {
      run.throttle = botThrottle(run);
      engine.step(1);
      this.handleEvents(run.drainEvents());
      if (until?.()) break;
    }
    run.throttle = 0;
  }

  private async applyTestState(name: string): Promise<void> {
    if (!(TEST_STATES as readonly string[]).includes(name)) throw new Error(`Unknown test state: ${name}`);
    // Test states set up a run directly: no menu or name screen in the way.
    this.el('#name-overlay').hidden = true;
    this.setMenuVisible(false);
    await this.restart();
    const run = this.sim.run;
    if (name === 'ready') {
      this.sim.engine.step(30);
    } else if (name === 'airborne') {
      this.simulateBot(60 * 40, () => {
        const r = run.readout;
        return run.state.ticks > 60 && !r.wheelGrounded[0] && !r.wheelGrounded[1] && r.y - run.terrain.heightAt(r.x) > 2.2;
      });
    } else if (name === 'kopdes') {
      // Just past the first Kopdes Merah Putih (750 m).
      this.simulateBot(60 * 160, () => run.state.distance >= 757);
    } else if (name === 'delivery') {
      // Drive to just past the first school (delivery mark) so its signboard and flag are in frame.
      this.simulateBot(60 * 120, () => run.state.distance >= 506);
    } else {
      this.simulateBot(60 * 6);
    }
    if (name === 'low-fuel') run.state.fuel = 12;
    if (name === 'run-summary') {
      const p = run.buggy.chassis.translation();
      run.buggy.teleport(p.x, p.y + 1.5, Math.PI);
      for (let i = 0; i < 180 && run.state.status !== 'ended'; i++) this.sim.engine.step(1);
    }
    this.handleEvents(run.drainEvents());
    this.driver.timeScale = 1;
    this.hitstopRemaining = 0;
    this.syncViews(1);
    if (name === 'run-summary') this.showSummary();
    if (name === 'paused') this.togglePause(true);
    if (name === 'map') {
      this.driver.paused = true;
      this.mapScreen.show(this.env.id, (id) => (id === 'kawah-bromo' ? 0 : 1200));
    }
    if (name === 'garage') {
      // Display-only sample garage (not saved), so captures show bought, affordable and locked cards.
      this.driver.paused = true;
      const sample = emptyGarage(245);
      sample.owned.push('motor');
      sample.upgrades.pickup = { engine: 2, tyres: 1, suspension: 0, tank: 5 };
      this.garageScreen.show(sample);
    }
    this.renderer.render(this.scene, this.camera);
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      // Routes are fixed per environment (design decision), so there is no run randomness to re-seed;
      // the value is recorded in diagnostics so captures show which seed was requested.
      seed: (value: number) => {
        this.requestedSeed = value;
      },
      setState: async (name: string) => {
        await this.applyTestState(name);
        return { state: name };
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.driver.paused = paused || this.overlays.pauseVisible;
      },
      setReducedMotion: (enabled: boolean) => {
        document.documentElement.classList.toggle('reduced-motion', enabled);
        this.cameraRig.reducedMotion = enabled;
        this.pickupsView.reducedMotion = enabled;
        this.particles.reducedMotion = enabled;
        this.renderer.render(this.scene, this.camera);
      },
      hideDebugUi: () => {
        // No debug UI ships in this build.
      },
    };
  }

  private publishDiagnostics(): void {
    const { run, engine } = this.sim;
    const r = run.readout;
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.driver.frame,
      elapsed: run.state.ticks / engine.tickRateHz,
      score: run.state.coins,
      targetScore: 0,
      complete: run.state.status === 'ended',
      player: { position: { x: r.x, y: r.y, z: 0 }, speed: Math.abs(r.forwardSpeed) },
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, MAX_DPR[this.quality]),
      },
      run: {
        envId: this.env.id,
        mapOpen: this.mapScreen.visible,
        menuOpen: this.menuVisible,
        audio: { state: this.sound.state, muted: this.sound.muted },
        ghost: this.ghost
          ? { active: true, distance: this.ghost.record.distance, x: this.ghost.sim.run.readout.x, ticks: this.ghost.sim.run.state.ticks, finished: this.ghost.finished }
          : { active: false, distance: 0, x: 0, ticks: 0, finished: false },
        seed: this.env.seed,
        requestedSeed: this.requestedSeed,
        status: run.state.status,
        endReason: run.state.endReason,
        distance: run.state.distance,
        fuel: run.state.fuel,
        coins: run.state.coins,
        flips: run.state.flips,
        wallet: this.garage.coins,
        upgrades: { ...currentLevels(this.garage) },
        vehicle: this.garage.vehicle,
        owned: [...this.garage.owned],
        garageOpen: this.garageScreen.visible,
        tick: engine.tick,
        angle: r.angle,
        grounded: r.wheelGrounded[0] || r.wheelGrounded[1],
        throttle: run.throttle,
        paused: this.driver.paused,
      },
    };
  }

  private el<T extends HTMLElement = HTMLElement>(selector: string): T {
    const found = document.querySelector<T>(selector);
    if (!found) throw new Error(`Missing element ${selector}`);
    return found;
  }
}

/** Saved graphics quality; first visit picks Low on phones with few cores or little memory. */
function initialQuality(): 'high' | 'low' {
  try {
    const saved = window.localStorage.getItem(QUALITY_KEY);
    if (saved === 'high' || saved === 'low') return saved;
  } catch {
    // Fall through to detection.
  }
  const nav = navigator as Navigator & { deviceMemory?: number };
  const touch = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const weak = (navigator.hardwareConcurrency ?? 8) <= 4 || (nav.deviceMemory ?? 8) <= 3;
  return touch && weak ? 'low' : 'high';
}

function readList(key: string): string[] {
  try {
    const v = JSON.parse(window.localStorage.getItem(key) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeValue(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Lasts for this session only.
  }
}

/** The saved environment if it is still unlocked, otherwise the first one. */
function startEnvironment(): EnvironmentDef {
  const saved = environmentById(loadSelectedEnv() ?? '');
  const forced = new URLSearchParams(window.location.search).get('env');
  const env = environmentById(forced ?? '') ?? saved ?? BUKIT_SAWAH;
  return forced || isUnlocked(env, loadBest) ? env : ENVIRONMENTS[0];
}

/** "BEST 640 m" (or "Racing #1: name") floating over the ghost; the plane is sized to the text. */
function makeGhostLabel(): THREE.Mesh {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 64;
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(1, 0.45),
    new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(canvas), transparent: true, depthWrite: false, opacity: 0.9 }),
  );
  mesh.renderOrder = 3;
  return mesh;
}

function setGhostLabel(mesh: THREE.Mesh, text: string): void {
  const map = (mesh.material as THREE.MeshBasicMaterial).map as THREE.CanvasTexture;
  const canvas = map.image as HTMLCanvasElement;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.font = '800 30px "Bahnschrift", "Arial Narrow", system-ui, sans-serif';
  const width = Math.min(canvas.width - 8, Math.ceil(ctx.measureText(text).width) + 40);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = 'rgba(31, 95, 168, 0.88)';
  ctx.beginPath();
  ctx.roundRect((canvas.width - width) / 2, 6, width, canvas.height - 12, 18);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 1);
  map.colorSpace = THREE.SRGBColorSpace;
  map.needsUpdate = true;
  // The plane is 0.45 m tall and the canvas 8:1, so it spans 3.6 m; the pill fills `width` of it.
  mesh.scale.set(3.6, 1, 1);
}

/** Radial-gradient blob under the car (cheap contact shadow; no extra shadow pass). */
function makeContactShadow(): THREE.Mesh {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(64, 32, 4, 64, 32, 62);
    g.addColorStop(0, 'rgba(30, 22, 12, 1)');
    g.addColorStop(1, 'rgba(30, 22, 12, 0)');
    ctx.setTransform(1, 0, 0, 0.5, 0, 16);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
  }
  const material = new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(canvas), transparent: true, depthWrite: false, opacity: 0.55 });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 1.3), material);
  mesh.renderOrder = 1;
  return mesh;
}
